import type { ReactNode } from 'react';
import { Dialog, Heading } from 'react-aria-components';
import { msg } from '@gonfalon/messages/msg';
import { IconButton } from '@launchpad-ui/components';
import clsx from 'clsx';

import { SearchField } from '../Form';
import { ListBox } from '../ListBox';
import type { ListBoxProps } from '../ListBox/ListBox';

import styles from './ListBoxDialog.module.css';

export type ListBoxDialogProps<T extends object> = Omit<ListBoxProps<T>, 'style'> & {
  title?: string;
  isBusy?: boolean;
  onFilterChange: (filterText: string) => void;
  afterSearch?: ReactNode;
};

export function ListBoxDialog<T extends object>({
  title,
  isBusy,
  onFilterChange,
  afterSearch,
  ...props
}: ListBoxDialogProps<T>) {
  return (
    <Dialog className={clsx(props.className, styles.dialog)} aria-label={props['aria-label']}>
      {({ close }) => (
        <>
          {title ? (
            <div className={styles.header}>
              <Heading level={3} slot="title">
                {title}
              </Heading>
              <IconButton
                aria-label={msg('ui.listBoxDialog.closeLabel')}
                variant="minimal"
                icon="cancel"
                size="small"
                onPress={close}
              />
            </div>
          ) : null}
          <SearchField
            className={styles.search}
            autoFocus
            aria-label={msg('ui.listBoxDialog.filterLabel')}
            isBusy={isBusy}
            onChange={onFilterChange}
          />
          {afterSearch ? (
            <div slot="afterSearch" className={styles.afterSearch}>
              {afterSearch}
            </div>
          ) : null}
          <ListBox
            {...props}
            className={styles.list}
            renderEmptyState={() => (isBusy ? msg('ui.listBoxDialog.searching') : msg('ui.listBoxDialog.noItemsFound'))}
          />
        </>
      )}
    </Dialog>
  );
}
